import { motion } from 'framer-motion';
import { LoaderCircle, Sparkles } from 'lucide-react';
import { marked } from 'marked';
import { useEffect, useState } from 'react';

import Transition from '@/components/Common/Transition';
import useColor from '@/hooks/domain/useColor';
import { Button as ShadcnButton } from '@/shared/ui';

const ANALYZE_CHANGE_DRIVERS_LABEL = 'Analyze Change Drivers';
const GUIDE_ROTATION_MS = 7_000;
const CHANGE_DRIVER_GUIDES = [
  'Change Driver Analysis compares this point with the previous value to find what moved most.',
  'Large datasets can take more than one minute to analyze, so keep this window open while it runs.',
  'The analysis uses the full filtered dataset to identify which values stand out at this point.',
  'Use the summary as a guide, then validate the likely drivers against the source feedback list.',
];

export type PeakAnalysisState = {
  status: 'idle' | 'loading' | 'success' | 'error';
  text?: string;
};

export const initialPeakAnalysisState: PeakAnalysisState = {
  status: 'idle',
};

type PeakAnalysisPanelProps = {
  state: PeakAnalysisState;
  onRetry: () => void;
};

export const PeakAnalysisPanel = ({
  state,
  onRetry,
}: PeakAnalysisPanelProps) => {
  const isLoading = state.status === 'loading';
  const isError = state.status === 'error';

  return (
    <section className="min-h-[300px] overflow-hidden rounded-md border border-slate-200 bg-white shadow-sm">
      <div className="flex h-12 items-center justify-between border-b border-slate-100 px-4">
        <div className="flex items-center gap-2">
          <span className="flex size-6 items-center justify-center rounded-md bg-primary/10 text-primary">
            <Sparkles className="size-3.5" />
          </span>
          <span className="text-sm font-semibold text-slate-800">
            {ANALYZE_CHANGE_DRIVERS_LABEL}
          </span>
          {isLoading && (
            <LoaderCircle className="size-4 shrink-0 animate-spin text-primary motion-reduce:animate-none" />
          )}
        </div>
        <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">
          {isLoading ? 'Analyzing' : isError ? 'Failed' : 'Complete'}
        </span>
      </div>
      {isLoading ? (
        <Transition status={isLoading}>
          <PeakAnalysisLoading />
        </Transition>
      ) : (
        <div className="flex min-h-[252px] flex-col justify-between gap-4 p-4">
          <Transition status={state.status === 'success'}>
            <div
              className="peak-analysis-markdown text-sm leading-6 text-slate-700 [&_h1]:mb-2 [&_h1]:text-base [&_h1]:font-semibold [&_h1]:text-slate-800 [&_h2]:mb-1 [&_h2]:text-sm [&_h2]:font-semibold [&_h2]:text-slate-800 [&_h3]:mb-1 [&_h3]:text-sm [&_h3]:font-medium [&_h3]:text-slate-800 [&_p]:mb-3 [&_ul]:mb-3 [&_ul]:list-disc [&_ul]:pl-4 [&_ol]:mb-3 [&_ol]:list-decimal [&_ol]:pl-4 [&_li]:mb-1 [&_strong]:font-semibold [&_a]:text-primary [&_a]:underline"
              dangerouslySetInnerHTML={{ __html: marked(state.text ?? '') }}
            />
          </Transition>
          {isError && (
            <Transition status={isError}>
              <ShadcnButton
                type="button"
                variant="outline"
                size="sm"
                onClick={onRetry}
              >
                Try again
              </ShadcnButton>
            </Transition>
          )}
        </div>
      )}
    </section>
  );
};

const PeakAnalysisLoading = () => {
  return (
    <div className="relative h-[300px] overflow-hidden p-4">
      <div className="relative z-10">
        <WordRotate
          words={CHANGE_DRIVER_GUIDES}
          duration={GUIDE_ROTATION_MS}
          className="mt-2 min-h-[44px] text-sm leading-6 text-slate-700"
        />
      </div>
    </div>
  );
};

const WordRotate = ({
  words,
  duration,
  className,
}: {
  words: string[];
  duration: number;
  className?: string;
}) => {
  const [index, setIndex] = useState(0);
  const word = words[index] ?? '';
  const color = useColor();
  const gradientStyle = `linear-gradient(90deg, ${color.gray700} 0%, ${color.gray600} 40%, ${color.gray400} 45%, ${color.gray400} 55%, ${color.gray600} 60%, ${color.gray700} 100%)`;

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % words.length);
    }, duration);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [duration, words.length]);

  return (
    <p className={className} aria-live="polite">
      <motion.span
        key={word}
        className="block"
        style={{
          background: gradientStyle,
          backgroundSize: '200% 100%',
          WebkitBackgroundClip: 'text',
          backgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          color: 'transparent',
        }}
        animate={{
          backgroundPosition: ['200% 0%', '-200% 0%'],
        }}
        transition={{
          duration: 1.8,
          repeat: Infinity,
          ease: 'easeOut',
        }}
      >
        {word}
      </motion.span>
    </p>
  );
};
